// ============================================================================
// REPORT SERVICE — Phase 1 skeleton
// ============================================================================
// Each report exposes list (paginated) + summary (single object) endpoints.
// All POST + body. Backend currently throws NotImplementedException → 501.

import { post } from '../client/axiosClient';
import type { ReportFormat } from 'utils/reportFile';
import { reportEndpoints } from '../endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  ReportFilter,
  ReportListRequest,
  BookingRegisterRequest,        BookingRegisterRow,            BookingRegisterSummary,
  ComplimentaryRow,              ComplimentarySummary,
  DiscountRow,                   DiscountSummary,
  MinRateViolationRow,           MinRateViolationSummary,
  MealPlanRevenueRow,            MealPlanRevenueSummary,
  ExtraPaxRow,                   ExtraPaxSummary,
  ForwardOccupancyRow,           ForwardOccupancySummary,
  AgentCorporateRow,             AgentCorporateSummary,
  DailyMonthlySummaryRow,        DailyMonthlySummarySummary,
  OccupancyChartRow,             OccupancyChartSummary,
  CheckinDetailsRow,             CheckinDetailsSummary,
  CollectionRow,                 CollectionSummary,
  GuestLedgerReport,             GuestLedgerLine,
  WaiverReport,
  CheckoutRegisterRequest,       CheckoutRegisterReport,
} from '../types';

/**
 * Standard backend `PagedResult<T>` → frontend `PaginatedResponse<T>`
 * transformation. Mirrors the convention used by all other services.
 */
function toPaginated<T>(raw: any): PaginatedResponse<T> {
  const pagedResult = raw?.data ?? {};
  return {
    success: raw?.success,
    message: raw?.message,
    data: pagedResult.items ?? [],
    pagination: {
      currentPage:    pagedResult.page ?? 1,
      pageSize:       pagedResult.pageSize ?? 25,
      totalPages:     pagedResult.totalPages ?? 0,
      totalItems:     pagedResult.totalCount ?? 0,
      hasNextPage:    pagedResult.hasNext ?? false,
      hasPreviousPage: pagedResult.hasPrevious ?? false,
    },
  };
}

class ReportService {
  // ── Booking Register ───────────────────────────────────────────────────────
  // Both endpoints take the same extended filter so KPIs always match the grid.
  async getBookingRegister(req: BookingRegisterRequest): Promise<PaginatedResponse<BookingRegisterRow>> {
    const r = await post<any>(reportEndpoints.bookingRegister.list(), req);
    return toPaginated<BookingRegisterRow>(r.data);
  }
  /** Excel / PDF built by the API — same filters as the screen. */
  async exportBookingRegister(filter: BookingRegisterRequest, format: ReportFormat) {
    const r = await post<Blob>(`${reportEndpoints.bookingRegister.exportFile()}?format=${format}`, filter, { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }
  async getBookingRegisterSummary(filter: BookingRegisterRequest): Promise<ApiResponse<BookingRegisterSummary>> {
    const r = await post<ApiResponse<BookingRegisterSummary>>(reportEndpoints.bookingRegister.summary(), filter);
    return r.data;
  }

  // ── Complimentary ──────────────────────────────────────────────────────────
  async getComplimentary(req: ReportListRequest): Promise<PaginatedResponse<ComplimentaryRow>> {
    const r = await post<any>(reportEndpoints.complimentary.list(), req);
    return toPaginated<ComplimentaryRow>(r.data);
  }
  async getComplimentarySummary(filter: ReportFilter): Promise<ApiResponse<ComplimentarySummary>> {
    const r = await post<ApiResponse<ComplimentarySummary>>(reportEndpoints.complimentary.summary(), filter);
    return r.data;
  }

  // ── Discount ───────────────────────────────────────────────────────────────
  async getDiscount(req: ReportListRequest): Promise<PaginatedResponse<DiscountRow>> {
    const r = await post<any>(reportEndpoints.discount.list(), req);
    return toPaginated<DiscountRow>(r.data);
  }
  async getDiscountSummary(filter: ReportFilter): Promise<ApiResponse<DiscountSummary>> {
    const r = await post<ApiResponse<DiscountSummary>>(reportEndpoints.discount.summary(), filter);
    return r.data;
  }

  // ── Min Rate Violation ─────────────────────────────────────────────────────
  async getMinRateViolation(req: ReportListRequest): Promise<PaginatedResponse<MinRateViolationRow>> {
    const r = await post<any>(reportEndpoints.minRateViolation.list(), req);
    return toPaginated<MinRateViolationRow>(r.data);
  }
  async getMinRateViolationSummary(filter: ReportFilter): Promise<ApiResponse<MinRateViolationSummary>> {
    const r = await post<ApiResponse<MinRateViolationSummary>>(reportEndpoints.minRateViolation.summary(), filter);
    return r.data;
  }

  // ── Meal Plan Revenue ──────────────────────────────────────────────────────
  async getMealPlanRevenue(req: ReportListRequest): Promise<PaginatedResponse<MealPlanRevenueRow>> {
    const r = await post<any>(reportEndpoints.mealPlanRevenue.list(), req);
    return toPaginated<MealPlanRevenueRow>(r.data);
  }
  async getMealPlanRevenueSummary(filter: ReportFilter): Promise<ApiResponse<MealPlanRevenueSummary>> {
    const r = await post<ApiResponse<MealPlanRevenueSummary>>(reportEndpoints.mealPlanRevenue.summary(), filter);
    return r.data;
  }

  // ── Extra Pax ──────────────────────────────────────────────────────────────
  async getExtraPax(req: ReportListRequest): Promise<PaginatedResponse<ExtraPaxRow>> {
    const r = await post<any>(reportEndpoints.extraPax.list(), req);
    return toPaginated<ExtraPaxRow>(r.data);
  }
  async getExtraPaxSummary(filter: ReportFilter): Promise<ApiResponse<ExtraPaxSummary>> {
    const r = await post<ApiResponse<ExtraPaxSummary>>(reportEndpoints.extraPax.summary(), filter);
    return r.data;
  }

  // ── Forward Occupancy ──────────────────────────────────────────────────────
  async getForwardOccupancy(req: ReportListRequest): Promise<PaginatedResponse<ForwardOccupancyRow>> {
    const r = await post<any>(reportEndpoints.forwardOccupancy.list(), req);
    return toPaginated<ForwardOccupancyRow>(r.data);
  }
  async getForwardOccupancySummary(filter: ReportFilter): Promise<ApiResponse<ForwardOccupancySummary>> {
    const r = await post<ApiResponse<ForwardOccupancySummary>>(reportEndpoints.forwardOccupancy.summary(), filter);
    return r.data;
  }

  // ── Agent / Corporate ──────────────────────────────────────────────────────
  async getAgentCorporate(req: ReportListRequest): Promise<PaginatedResponse<AgentCorporateRow>> {
    const r = await post<any>(reportEndpoints.agentCorporate.list(), req);
    return toPaginated<AgentCorporateRow>(r.data);
  }
  async getAgentCorporateSummary(filter: ReportFilter): Promise<ApiResponse<AgentCorporateSummary>> {
    const r = await post<ApiResponse<AgentCorporateSummary>>(reportEndpoints.agentCorporate.summary(), filter);
    return r.data;
  }

  // ── Daily / Monthly Summary ────────────────────────────────────────────────
  async getDailyMonthlySummary(req: ReportListRequest): Promise<PaginatedResponse<DailyMonthlySummaryRow>> {
    const r = await post<any>(reportEndpoints.dailyMonthlySummary.list(), req);
    return toPaginated<DailyMonthlySummaryRow>(r.data);
  }
  async getDailyMonthlySummarySummary(filter: ReportFilter): Promise<ApiResponse<DailyMonthlySummarySummary>> {
    const r = await post<ApiResponse<DailyMonthlySummarySummary>>(reportEndpoints.dailyMonthlySummary.summary(), filter);
    return r.data;
  }

  // ── Occupancy Chart (single date — caller sends from = to = chart date) ────
  async getOccupancyChart(req: ReportListRequest): Promise<PaginatedResponse<OccupancyChartRow>> {
    const r = await post<any>(reportEndpoints.occupancyChart.list(), req);
    return toPaginated<OccupancyChartRow>(r.data);
  }
  async getOccupancyChartSummary(filter: ReportFilter): Promise<ApiResponse<OccupancyChartSummary>> {
    const r = await post<ApiResponse<OccupancyChartSummary>>(reportEndpoints.occupancyChart.summary(), filter);
    return r.data;
  }

  // ── Check-in Details (registration register, check-in date range) ──────────
  async getCheckinDetails(req: ReportListRequest): Promise<PaginatedResponse<CheckinDetailsRow>> {
    const r = await post<any>(reportEndpoints.checkinDetails.list(), req);
    return toPaginated<CheckinDetailsRow>(r.data);
  }
  async getCheckinDetailsSummary(filter: ReportFilter): Promise<ApiResponse<CheckinDetailsSummary>> {
    const r = await post<ApiResponse<CheckinDetailsSummary>>(reportEndpoints.checkinDetails.summary(), filter);
    return r.data;
  }

  // ── Formatted report files (PMS slice 5): .xlsx to save, PDF to print ──────
  async exportOccupancyChart(req: ReportFilter & { includeDepartures?: boolean }, format: ReportFormat) {
    const r = await post<Blob>(`${reportEndpoints.occupancyChart.exportFile()}?format=${format}`, req, { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }
  async exportCollection(filter: ReportFilter, format: ReportFormat) {
    const r = await post<Blob>(`${reportEndpoints.collection.exportFile()}?format=${format}`, filter, { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }

  // ── Relaxations & Waivers (PMS slice 7c-2) ──
  async getWaivers(filter: ReportFilter): Promise<WaiverReport> {
    const r = await post<ApiResponse<WaiverReport>>(reportEndpoints.waivers.report(), filter);
    if (!r.data?.data) throw new Error(r.data?.message || 'The report did not load.');
    return r.data.data;
  }
  async exportWaivers(filter: ReportFilter, format: ReportFormat) {
    const r = await post<Blob>(`${reportEndpoints.waivers.exportFile()}?format=${format}`, filter, { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }

  // ── Checkout Register (BR-RPT-003..006) ──
  async getCheckoutRegister(req: CheckoutRegisterRequest): Promise<CheckoutRegisterReport> {
    const r = await post<ApiResponse<CheckoutRegisterReport>>(reportEndpoints.checkoutRegister.report(), req);
    if (!r.data?.data) throw new Error(r.data?.message || 'The report did not load.');
    return r.data.data;
  }
  async exportCheckoutRegister(req: CheckoutRegisterRequest, format: ReportFormat) {
    const r = await post<Blob>(`${reportEndpoints.checkoutRegister.exportFile()}?format=${format}`, req, { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }

  // ── Guest Ledger — Posted vs Billed (PMS slice 6c) ──
  async getGuestLedger(filter: ReportFilter): Promise<GuestLedgerReport> {
    const r = await post<ApiResponse<GuestLedgerReport>>(reportEndpoints.guestLedger.report(), filter);
    if (!r.data?.data) throw new Error(r.data?.message || 'The guest ledger did not load.');
    return r.data.data;
  }
  /** The movements of one business date (filter.from). */
  async getGuestLedgerLines(propertyId: string, date: string): Promise<GuestLedgerLine[]> {
    const r = await post<ApiResponse<GuestLedgerLine[]>>(reportEndpoints.guestLedger.lines(), { propertyId, from: date, to: date });
    return r.data?.data ?? [];
  }
  async exportGuestLedger(filter: ReportFilter, format: ReportFormat) {
    const r = await post<Blob>(`${reportEndpoints.guestLedger.exportFile()}?format=${format}`, filter, { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }
  /** One business date's lines — the drilled view. */
  async exportGuestLedgerLines(propertyId: string, date: string, format: ReportFormat) {
    const r = await post<Blob>(`${reportEndpoints.guestLedger.exportLines()}?format=${format}`, { propertyId, from: date, to: date }, { responseType: 'blob' });
    return { blob: r.data, disposition: r.headers?.['content-disposition'] as string | undefined };
  }

  // ── Collection (receipts and refunds by mode, payment-date range — PMS slice 5) ──
  async getCollection(req: ReportListRequest): Promise<PaginatedResponse<CollectionRow>> {
    const r = await post<any>(reportEndpoints.collection.list(), req);
    return toPaginated<CollectionRow>(r.data);
  }
  async getCollectionSummary(filter: ReportFilter): Promise<ApiResponse<CollectionSummary>> {
    const r = await post<ApiResponse<CollectionSummary>>(reportEndpoints.collection.summary(), filter);
    return r.data;
  }
}

export const reportService = new ReportService();
export default reportService;
